/**
 * Image Security Utilities
 * Provides validation and sanitization for image processing to prevent
 * resource consumption attacks and malicious image URL injection
 */

// Maximum allowed image dimensions to prevent excessive memory usage
const MAX_IMAGE_WIDTH = 4096;
const MAX_IMAGE_HEIGHT = 4096;
const MAX_IMAGE_SIZE_MB = 10;
const MAX_PROCESSING_TIME_MS = 30000; // 30 seconds

// // Allowed image MIME types
// const ALLOWED_IMAGE_TYPES = [
//   'image/jpeg',
//   'image/jpg',
//   'image/png',
//   'image/gif',
//   'image/webp',
//   'image/svg+xml'
// ];

// Allowed URL protocols for image sources
const ALLOWED_PROTOCOLS = ['data:', 'blob:', 'https:', 'http:'];

/**
 * Validates image dimensions and file size
 * @param {HTMLImageElement} img - Image element to validate
 * @returns {Promise<boolean>} - True if image passes validation
 */
export const validateImageDimensions = async (img) => {
  return new Promise((resolve) => {
    img.onload = () => {
      const isValidSize =
        img.width <= MAX_IMAGE_WIDTH && img.height <= MAX_IMAGE_HEIGHT;
      resolve(isValidSize);
    };
    img.onerror = () => resolve(false);
  });
};

/**
 * Validates image URL for security
 * @param {string} url - Image URL to validate
 * @returns {boolean} - True if URL is safe
 */
export const validateImageUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  // Check if URL uses allowed protocol
  const hasValidProtocol = ALLOWED_PROTOCOLS.some((protocol) =>
    url.toLowerCase().startsWith(protocol)
  );

  if (!hasValidProtocol) {
    return false;
  }

  // Additional checks for data URLs
  if (url.startsWith('data:')) {
    // Ensure it's a valid image data URL
    const dataUrlPattern =
      /^data:image\/(jpeg|jpg|png|gif|webp|svg\+xml);base64,/i;
    if (!dataUrlPattern.test(url)) {
      return false;
    }

    // Check data URL size (rough estimate)
    const base64Data = url.split(',')[1];
    if (base64Data) {
      const sizeInBytes = (base64Data.length * 3) / 4;
      const sizeInMB = sizeInBytes / (1024 * 1024);
      if (sizeInMB > MAX_IMAGE_SIZE_MB) {
        return false;
      }
    }
  }

  return true;
};

/**
 * Sanitizes canvas data URL before processing
 * @param {string} dataUrl - Canvas data URL
 * @returns {Promise<string|null>} - Sanitized data URL or null if invalid
 */
export const sanitizeCanvasDataUrl = async (dataUrl) => {
  if (!validateImageUrl(dataUrl)) {
    return null;
  }

  try {
    // Create a temporary image to validate the data
    const img = new Image();
    img.src = dataUrl;

    const isValid = await validateImageDimensions(img);

    if (!isValid) {
      return null;
    }

    return dataUrl;
  } catch (error) {
    console.warn('Image sanitization failed:', error);
    return null;
  }
};

/**
 * Creates a timeout wrapper for image processing operations
 * @param {Function} operation - Async operation to wrap
 * @param {number} timeoutMs - Timeout in milliseconds
 * @returns {Promise} - Promise that resolves/rejects with timeout
 */
export const withTimeout = (operation, timeoutMs = MAX_PROCESSING_TIME_MS) => {
  return Promise.race([
    operation(),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Operation timed out')), timeoutMs)
    ),
  ]);
};

/**
 * Validates and processes image for PDF generation
 * @param {string} imageData - Image data URL
 * @param {Object} options - Processing options
 * @returns {Promise<string|null>} - Processed image data or null if invalid
 */
export const validateAndProcessImageForPdf = async (
  imageData,
  options = {}
) => {
  const { maxWidth = MAX_IMAGE_WIDTH, maxHeight = MAX_IMAGE_HEIGHT } = options;

  try {
    // Validate the image URL
    if (!validateImageUrl(imageData)) {
      throw new Error('Invalid image URL');
    }

    // Create image element for validation
    const img = new Image();
    img.crossOrigin = 'anonymous'; // Enable CORS for external images

    const validationPromise = new Promise((resolve, reject) => {
      img.onload = () => {
        // Check dimensions
        if (img.width > maxWidth || img.height > maxHeight) {
          reject(
            new Error(
              `Image dimensions exceed limits: ${img.width}x${img.height}`
            )
          );
          return;
        }

        // If image is too large, resize it
        if (img.width > maxWidth || img.height > maxHeight) {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Calculate new dimensions maintaining aspect ratio
          const aspectRatio = img.width / img.height;
          let newWidth = Math.min(img.width, maxWidth);
          let newHeight = Math.min(img.height, maxHeight);

          if (newWidth / aspectRatio > newHeight) {
            newWidth = newHeight * aspectRatio;
          } else {
            newHeight = newWidth / aspectRatio;
          }

          canvas.width = newWidth;
          canvas.height = newHeight;

          ctx.drawImage(img, 0, 0, newWidth, newHeight);
          resolve(canvas.toDataURL('image/png', 0.9));
        } else {
          resolve(imageData);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
    });

    img.src = imageData;

    // Apply timeout to prevent hanging
    return await withTimeout(() => validationPromise);
  } catch (error) {
    console.warn('Image validation failed:', error.message);
    return null;
  }
};

/**
 * Resource monitor for tracking processing resources
 */
export class ResourceMonitor {
  constructor() {
    this.startTime = null;
    this.memoryStart = null;
  }

  start() {
    this.startTime = Date.now();
    if (performance.memory) {
      this.memoryStart = performance.memory.usedJSHeapSize;
    }
  }

  check() {
    const elapsed = Date.now() - this.startTime;

    if (elapsed > MAX_PROCESSING_TIME_MS) {
      throw new Error('Processing time limit exceeded');
    }

    if (performance.memory && this.memoryStart) {
      const memoryUsed = performance.memory.usedJSHeapSize - this.memoryStart;
      const memoryLimitBytes = MAX_IMAGE_SIZE_MB * 1024 * 1024 * 5; // 5x the image size limit

      if (memoryUsed > memoryLimitBytes) {
        throw new Error('Memory usage limit exceeded');
      }
    }

    return {
      elapsed,
      memoryUsed: performance.memory
        ? performance.memory.usedJSHeapSize - this.memoryStart
        : 0,
    };
  }
}
